import 'rc-slider/assets/index.css'
import Big from 'big.js'
import { toUnit } from 'metronome-utils'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Slider from 'rc-slider'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useExecuteTransactions } from '../hooks/useExecuteTransactions'

import { AppContext } from './AppContext/AppContext'
import CryptoInputGroup from './CryptoInputGroup/CryptoInputGroup'
import { LockedMETContext } from './LockedMetContext'
import LockFormDetails from './LockFormDetails'
import { MetronomeContext } from './MetronomeContext'
import OperationButton from './OperationButton'
import RewardsSummary from './RewardsSummary'
import TransactionContext from './TransactionContext'

const lockPeriods = [
  { days: 7, label: '1W' },
  { days: 180, label: '6M' },
  { days: 365, label: '1Y' },
  { days: 545, label: '1.5Y' },
  { days: 730, label: '2Y' }
]

const secondsInADay = 86400

function LockForm() {
  const { t } = useTranslation()
  const router = useRouter()
  const { shortName } = router.query
  const {
    state: {
      status: { paused },
      wallet: { connected, balancesLoaded }
    }
  } = useContext(AppContext)

  const {
    disableOnOperation,
    openTransactionDrawer,
    setDisableOnOperation,
    setOperationExecuted
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useContext<any>(MetronomeContext) // @TODO: type MetronomeContext

  const { amountLocked, closeOperation } = useContext(LockedMETContext)

  const {
    metLocker,
    met,
    minimumLockPeriod = 0,
    maximumLockPeriod = lockPeriods[lockPeriods.length - 1].days * secondsInADay
  } = useContext(LockedMETContext)
  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    reset,
    setFocus,
    watch
  } = useForm({
    defaultValues: {
      amount: '',
      lockPeriod: 7,
      token: met
    },
    mode: 'onChange'
  })

  useEffect(
    function () {
      setFocus('amount')
    },
    [setFocus]
  )

  const { options: transactionOptions } = useContext(TransactionContext)
  const { executeTransactions } = useExecuteTransactions()

  const availableTokens = useMemo(() => (met ? [met] : []), [met])

  const formatExpireDate = useCallback(
    lockPeriodInSeconds => new Date(Date.now() + lockPeriodInSeconds * 1000),
    []
  )

  const minimumLockPeriodInDays = Big(minimumLockPeriod)
    .div(secondsInADay)
    .round()
    .toNumber()
  const maximumLockPeriodInDays = Big(maximumLockPeriod)
    .div(secondsInADay)
    .round()
    .toNumber()

  const onSubmit = useCallback(
    function ({ amount, token, lockPeriod }) {
      if (!met) {
        return
      }

      openTransactionDrawer('lock')
      closeOperation('lock')
      setDisableOnOperation(true)
      setOperationExecuted(true)
      const _transactionOptions = {
        useEip1559: transactionOptions.useEip1559
      }

      // the lockPeriod must be greater than the minimum, so we add 1 if it is the minimum
      let lockPeriodInSeconds = lockPeriod * secondsInADay
      if (lockPeriod === minimumLockPeriodInDays) {
        lockPeriodInSeconds++
      }

      const { emitter } = metLocker.lock(
        toUnit(Big(amount).toFixed(), met.decimals),
        lockPeriodInSeconds,
        _transactionOptions
      )

      const operationSubject = `${Big(amount).toFixed(4)} ${met.symbol}`
      const analyticsId = operationSubject
      executeTransactions(emitter, {
        analytics: {
          events: {
            'error': `Locking failed for ${analyticsId}`,
            'error-timeout': `Locking timeout in wallet for ${analyticsId}`,
            'result': `Locking succeeded for ${analyticsId}`,
            'transactions': `Locking initiated for ${analyticsId}`
          },
          toast: {
            close: `Closing pending tx notification for Locking of ${analyticsId}`,
            reopen: `Reopening Tx Modal for Locking of ${analyticsId}`
          },
          useEip1559: `Using EIP-1559 to Lock MET`
        },
        events: {
          extendedStatus: {
            amount,
            expireDate: formatExpireDate(lockPeriodInSeconds),
            lockPeriod,
            operation: 'lock',
            operationSubject,
            token,
            totalAmountLocked: amountLocked
          }
        },
        onError() {
          setDisableOnOperation(false)
        },
        onSuccess() {
          router.push(`/${shortName}/lock-met/positions`, undefined, {
            shallow: true
          })
          setDisableOnOperation(false)
          reset()
        }
      })
    },
    [
      amountLocked,
      closeOperation,
      executeTransactions,
      formatExpireDate,
      met,
      metLocker,
      minimumLockPeriodInDays,
      openTransactionDrawer,
      reset,
      router,
      setDisableOnOperation,
      setOperationExecuted,
      shortName,
      transactionOptions.useEip1559
    ]
  )

  const marks = Object.fromEntries(
    lockPeriods.map(({ days, label }, index) => [
      days,
      {
        label: <span className="text-xs text-gray-600">{label}</span>,
        style: {
          ...(index === 0
            ? { marginLeft: '5px' }
            : index === lockPeriods.length - 1
            ? { marginLeft: '-5px' }
            : {}),
          marginTop: '2px',
          top: '5px'
        }
      }
    ])
  )

  const lockPeriod = watch('lockPeriod')

  const hasErrors = Object.keys(errors).length > 0
  const expireDate = formatExpireDate(lockPeriod * secondsInADay)
  const userBalance = watch('token')?.balance || 0
  const validateInput =
    touchedFields.amount && errors.amount?.type === 'greaterThanZero'

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="sm:space-x-6 md:flex">
        <div className="bg-block w-full space-y-4 rounded-md p-6 text-white md:w-7/12">
          <div className="bg-blue-secondary rounded-lg p-4">
            <span className="text-lg font-medium">
              {t('lock-form.lock-amount')}
            </span>
            <CryptoInputGroup availableTokens={availableTokens}>
              <div
                className={`bg-blue-5 mt-6 rounded-lg border px-4 pb-3 pt-4 ${
                  validateInput ? 'border-red-600' : 'border-blue-secondary'
                }`}
              >
                <div className="flex justify-between">
                  <Controller
                    control={control}
                    name="amount"
                    render={({ field }) => (
                      <CryptoInputGroup.Input
                        {...field}
                        autoComplete="off"
                        className={
                          validateInput ? 'caret-red-600' : 'caret-teal-primary'
                        }
                        disabled={disableOnOperation || paused}
                      />
                    )}
                    rules={{
                      validate: {
                        greaterThanZero: value => value && Big(value).gt(0),
                        max: value => value && Big(value).lte(userBalance)
                      }
                    }}
                  />

                  <Controller
                    control={control}
                    name="token"
                    render={({ field: { onChange, onBlur } }) => (
                      <CryptoInputGroup.TokenSelector
                        chooseLabel={t('choose-collateral')}
                        disabled={disableOnOperation || paused}
                        onBlur={onBlur}
                        onChange={onChange}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </div>

                <div className="text-gray-4 mt-2 flex items-center justify-between text-sm leading-6">
                  <CryptoInputGroup.UsdValue prefix="≈$" />
                  <CryptoInputGroup.MaxAmount
                    disabled={disableOnOperation}
                    isValid={errors.amount?.type !== 'max'}
                    loading={!balancesLoaded}
                    prefix={t('wallet')}
                  />
                </div>
              </div>
            </CryptoInputGroup>
          </div>

          <div className="bg-blue-secondary rounded-lg p-4">
            <span className="text-lg font-medium">
              {t('lock-form.choose-locking-time')}
            </span>
            <Controller
              control={control}
              name="lockPeriod"
              render={({ field: { onChange, value } }) => (
                <div className="bg-blue-5 mt-6 rounded-lg px-4 pb-3 pt-4">
                  <span className="mb-4 block font-semibold">
                    <Trans
                      components={[
                        <span className="text-teal-primary" key={0} />
                      ]}
                      i18nKey="lock-form.lock-for-time"
                    />
                    <span
                      className={`${
                        errors.lockPeriod ? 'text-red-600' : 'text-teal-primary'
                      } font-semibold`}
                    >
                      <input
                        className="
                          !mx-1 w-[3ch] border-0 bg-transparent text-right font-semibold
                          focus-visible:outline-none
                        "
                        onBlur={function (e) {
                          if (e.target.value === '') {
                            onChange(minimumLockPeriodInDays)
                          }
                        }}
                        onChange={function (e) {
                          // this prevents from typing characters other than
                          // numbers and limits to 3 digits
                          if (
                            /^\d{0,3}$/.test(e.target.value) ||
                            e.target.value === ''
                          ) {
                            return onChange(e)
                          }
                          return null
                        }}
                        value={value}
                      />
                      {t('days')}
                      {errors?.lockPeriod?.message && (
                        <span className="ml-1 text-sm">
                          ({errors?.lockPeriod?.message})
                        </span>
                      )}
                    </span>
                  </span>
                  <div className="select-none px-1">
                    <Slider
                      className="!bg-transparent text-white"
                      dotStyle={{
                        display: 'none'
                      }}
                      handleStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '4px solid #259DA8',
                        boxShadow: 'none',
                        height: '24px',
                        marginTop: '-10px',
                        opacity: '1',
                        width: '24px'
                      }}
                      marks={marks}
                      max={maximumLockPeriodInDays}
                      min={minimumLockPeriodInDays}
                      onChange={onChange}
                      step={1}
                      trackStyle={{
                        backgroundColor: '#259DA8'
                      }}
                      value={value}
                    />
                  </div>

                  <div className="mt-14 flex justify-between text-base">
                    <span>{`${t('expire-date')}:`}</span>
                    <span>{expireDate.toLocaleDateString()}</span>
                  </div>
                </div>
              )}
              rules={{
                max: {
                  message: t('lock-period-max', {
                    count: maximumLockPeriodInDays
                  }),
                  value: maximumLockPeriodInDays
                },
                min: {
                  message: t('lock-period-min', {
                    count: minimumLockPeriodInDays
                  }),
                  value: minimumLockPeriodInDays
                }
              }}
            />
            <LockFormDetails
              amountToLock={
                watch('amount') ? Big(watch('amount')).toNumber() : 0
              }
              lockPeriodInSeconds={lockPeriod * secondsInADay}
            />
            <OperationButton
              className="mt-6"
              disabled={hasErrors || !connected}
              type="submit"
            >
              {t('actions.lock')}
            </OperationButton>
          </div>
        </div>

        <RewardsSummary />
      </div>
    </form>
  )
}

export default LockForm
